import React from 'react';
import PropTypes from 'prop-types';

import {Button, FiveStar, Paper, Stack, SwitchCheckbox, TextField, Typography} from 'views/components';

import {DatePicker, Translate, translate} from 'views/module';

import moment from 'moment';

const AddReview = (props) => {
  const [reviewerName, setReviewerName] = React.useState('');
  const [relationship, setRelationship] = React.useState('');
  const [date, setDate] = React.useState(() => moment());
  const [rating, setRating] = React.useState(5.0);
  const [isVerified, setIsVerified] = React.useState(false);
  const [text, setText] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    caregiverBackend,
    setIsAddingReview,
  } = props;

  const handleSave = () => {
    const formattedDate = date.toISOString();
    setIsLoading(true);
    const body = {
      reviewerName,
      relationship,
      date: formattedDate,
      rating,
      isVerified,
      text,
    };

    const onSuccess = () => {
      setIsLoading(false);
      setIsAddingReview(false);
    };

    caregiverBackend.reviews.createCaregiverReview(body, onSuccess);
  };

  const handleClose = () => {
    setReviewerName('');
    setDate(null);
    setRating(5.0);
    setIsVerified(false);
    setText('');
    setIsAddingReview(false);
  };

  const isValid = React.useMemo(() => {
    return Boolean(reviewerName.trim() && relationship.trim() && date && rating && text.trim());
  }, [reviewerName, relationship, date, rating, text]);

  return (

    <Paper
      variant="outlined"
      sx={{
        borderColor: 'rgba(252, 131, 1, 0.3)',
        borderWidth: 2,
        width: '100%',
        p: 2,
      }}>
      <Stack spacing={2} sx={{py: 1}}>
        {/* Reviewer Name */}
        <TextField
          label={translate('caregiver.CaregiverReview.AddReview.ReviewerName')}
          placeholder={translate('caregiver.CaregiverReview.AddReview.ReviewerNamePlaceholder')}
          value={reviewerName}
          onChange={setReviewerName}
          fullWidth
          variant="outlined"
        />

        {/* Relationship */}
        <TextField
          label={translate('caregiver.CaregiverReview.AddReview.Relationship')}
          placeholder={translate('caregiver.CaregiverReview.AddReview.RelationshipPlaceholder')}
          value={relationship}
          onChange={setRelationship}
          fullWidth
          variant="outlined"
        />

        {/* Date */}
        <DatePicker
          label={translate('caregiver.CaregiverReview.AddReview.Date')}
          value={date}
          onChange={setDate}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
            },
          }}
        />

        {/* Rating */}
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{fontWeight: 600}}>
            <Translate expression="caregiver.CaregiverReview.AddReview.Rating" />
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FiveStar
              value={rating}
              onChange={setRating}
              precision={0.5}
              size="large"
              sx={{color: 'primary.main'}}
            />
            <Typography variant="body1" sx={{fontWeight: 500, minWidth: 30}}>
              {rating.toFixed(1)}
            </Typography>
          </Stack>
        </Stack>

        {/* Verified Review Toggle */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
          <SwitchCheckbox
            onChange={setIsVerified}
            value={isVerified}
          />
          <Typography variant="subtitle2" sx={{fontWeight: 600}}>
            <Translate expression="caregiver.CaregiverReview.AddReview.VerifiedReview" />
          </Typography>
        </Stack>

        {/* Review Text */}
        <TextField
          label={translate('caregiver.CaregiverReview.AddReview.Text')}
          placeholder={translate('caregiver.CaregiverReview.AddReview.TextPlaceholder')}
          value={text}
          onChange={setText}
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          variant="outlined"
        />

        {/* Cancel & Save */}
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{minWidth: 100}}
          >
            <Translate expression="caregiver.CaregiverReview.AddReview.Cancel" />
          </Button>
          <Button
            color="primary"
            disabled={!isValid || isLoading}
            onClick={handleSave}
            loading={isLoading}
            sx={{
              'minWidth': 100,
              'backgroundColor': 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <Translate expression="caregiver.CaregiverReview.AddReview.Save" />
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

AddReview.propTypes = {
  caregiverRn: PropTypes.string.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  isAddingReview: PropTypes.bool.isRequired,
  setIsAddingReview: PropTypes.func.isRequired,
};

export default React.memo(AddReview);
